import { useCallback, useEffect, useRef, useState } from "react";
import { ApiKeyField } from "./settings/ApiKeyField";
import { apiDelete, apiGet, apiPatch, apiPut, ApiError } from "../api/client";
import {
  capNote, isoDateTimeText, MM_REFRESH_MAX, MM_REFRESH_MIN, MM_SETTINGS_INTRO, parseRefreshMinutes, resultText, stateText, utcMidnightText,
  type MeshMapperStatus,
} from "../lib/meshmapperCoverage";

// Settings, MeshMapper: the API key (masked, set, replace or clear), the on/off switch for the map layer, how often to
// refresh, and what the last fetches did. Saves apply at once: no restart. The key is sent once and never shown again;
// the page only ever learns whether one is set.

type Note = { kind: "ok" | "error"; text: string } | null;

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const m = /—\s(.+)$/.exec(e.message);
    if (m) return m[1];
    if (e.status === 403) return "Only an admin can change this.";
    return "That did not save. Try again.";
  }
  return "That did not save. Try again.";
}

export function MeshMapperSettings() {
  const [status, setStatus] = useState<MeshMapperStatus | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [minutesText, setMinutesText] = useState("");
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState(false);
  const minutesEdited = useRef(false);

  const load = useCallback(async () => {
    try {
      const s = await apiGet<MeshMapperStatus>("/api/meshmapper/status");
      setStatus(s);
      setLoadFailed(false);
      if (!minutesEdited.current) setMinutesText(String(s.refresh_minutes));
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 20_000);
    return () => window.clearInterval(t);
  }, [load]);

  async function run(action: () => Promise<unknown>, okText: string) {
    setBusy(true);
    setNote(null);
    try {
      await action();
      setNote({ kind: "ok", text: okText });
      await load();
    } catch (e) {
      setNote({ kind: "error", text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function saveKey(key: string) {
    setNote(null);
    await run(() => apiPut("/api/meshmapper/key", { api_key: key }), "Key saved. Stored coverage from the old key was removed.");
  }

  async function clearKey() {
    setNote(null);
    await run(() => apiDelete("/api/meshmapper/key"), "Key cleared. Stored coverage was removed.");
  }

  async function setEnabled(enabled: boolean) {
    await run(() => apiPatch("/api/config/meshmapper", { patch: { enabled } }), enabled ? "Coverage is on." : "Coverage is off.");
  }

  async function saveMinutes() {
    const n = parseRefreshMinutes(minutesText);
    if (n === null) {
      setNote({ kind: "error", text: `Use a whole number from ${MM_REFRESH_MIN} to ${MM_REFRESH_MAX}.` });
      return;
    }
    if (status && n === status.refresh_minutes) {
      minutesEdited.current = false;
      return;
    }
    await run(() => apiPatch("/api/config/meshmapper", { patch: { refresh_minutes: n } }), "Refresh time saved.");
    minutesEdited.current = false;
  }

  const typedMinutes = parseRefreshMinutes(minutesText);
  const capText = status ? capNote(typedMinutes ?? status.refresh_minutes, status.daily_cap, Date.now()) : null;

  if (!status) {
    return <div className="mm-settings">{loadFailed ? <div className="node-detail-note">Could not load the MeshMapper settings.</div> : <div className="empty-state">Loading…</div>}</div>;
  }

  return (
    <div className="mm-settings">
      <p className="mm-settings-intro">{MM_SETTINGS_INTRO}</p>

      <ApiKeyField
        labelId="mm-key-label"
        keySet={status.key_set}
        busy={busy}
        pastePlaceholder="Paste the MeshMapper API key"
        description="The key is kept on the server and never shown again. Changing or clearing it removes the coverage stored for the old key."
        clearQuestion="Remove the key and its stored coverage?"
        onSave={saveKey}
        onClear={clearKey}
        onEmpty={() => setNote({ kind: "error", text: "Paste the key first." })}
      />

      <label className="settings-field mm-settings-block mm-settings-switch">
        <input type="checkbox" checked={status.enabled} disabled={busy} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Show MeshMapper coverage</span>
        <span className="settings-field-desc">Draws the coverage as hexes on the Live Map. Off by default; while it is off nothing is requested from MeshMapper. It also needs a key.</span>
      </label>

      <div className="settings-field mm-settings-block">
        <label htmlFor="mm-refresh">Refresh every (minutes)</label>
        <div className="mm-settings-keyrow">
          <input
            id="mm-refresh"
            type="number"
            min={MM_REFRESH_MIN}
            max={MM_REFRESH_MAX}
            step={1}
            value={minutesText}
            onChange={(e) => { minutesEdited.current = true; setMinutesText(e.target.value); }}
            onBlur={saveMinutes}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          />
        </div>
        <span className="settings-field-desc">From {MM_REFRESH_MIN} to {MM_REFRESH_MAX}. MeshMapper itself updates about every 15 minutes, and this app never makes more than {status.daily_cap} requests a day.</span>
      </div>

      {capText && (
        <div className="mm-settings-capnote" role="note">
          {capText}
        </div>
      )}

      {note && (
        <div className="settings-save-status" data-kind={note.kind} role="status">
          {note.text}
        </div>
      )}

      <h3 className="mm-settings-heading">Status</h3>
      <dl className="mm-settings-status">
        <div><dt>State</dt><dd>{stateText(status.state)}</dd></div>
        <div><dt>Last fetch</dt><dd>{isoDateTimeText(status.last_fetch_at)}{status.last_result ? ` (${resultText(status.last_result)})` : ""}</dd></div>
        <div><dt>Region</dt><dd>{status.region_name ?? status.region ?? "none yet"}</dd></div>
        <div><dt>MeshMapper squares</dt><dd>{status.squares.toLocaleString("en-US")}</dd></div>
        <div><dt>Requests today</dt><dd>{status.requests_today} of {status.daily_cap} (resets at {utcMidnightText(Date.now())})</dd></div>
        <div><dt>Next fetch</dt><dd>{status.next_poll_at ? isoDateTimeText(status.next_poll_at) : "not scheduled"}</dd></div>
        <div><dt>Last error</dt><dd className={status.last_error ? "mm-settings-error" : undefined}>{status.last_error ?? "none"}</dd></div>
      </dl>
    </div>
  );
}
